import styled from '@emotion/styled';
import { Button, Input, Radio, Select } from 'antd';
import { useRef, useState } from 'react';
import ImageUploader, { accepts } from './ImageUploader';
import PdfUploader from './PdfUploader';
import VideoUploader from './VideoUploader';
import { useTranslate } from '@tolgee/react';
import { UploadOutlined } from '@ant-design/icons';

const SelectWrapper = styled.div`
  width: 120px;
  margin-bottom: 8px;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: stretch;
  > * {
    flex: 1 0 0;
    & + * {
      margin-left: 12px;
    }
  }
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border: solid 1px var(--fett-border-strong);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  > label {
    display: flex;
    align-items: center;
    height: 46px;
    padding: 0 12px;
    margin-right: 0;
    background: var(--fett-erp-card-bg);
    .ant-radio {
      margin-top: -4px;
    }
  }
  > p {
    padding: 4px 12px;
    font-size: 12px;
    color: var(--fett-text-quaternary);
  }
`;

const UploadWrapper = styled.div`
  margin-top: 8px;
`;

function HeaderEditor({
  value,
  onChange,
  onBlur,
}: {
  value?: Aide.WhatsAppHeader;
  onChange: (value?: Aide.WhatsAppHeader) => void;
  onBlur: () => void;
}) {
  const { t } = useTranslate();

  type OptionType = 'text' | 'material';

  const [type, setType] = useState<OptionType | undefined>(() => {
    if (!value) {
      return undefined;
    }

    if (value.type === 'text') {
      return 'text';
    }

    return 'material';
  });

  const select = (
    <SelectWrapper>
      <Select
        allowClear
        placeholder={t('无')}
        value={type}
        onChange={(type) => {
          setType(type);

          if (type === 'text') {
            onChange({
              type: 'text',
              text: '',
            });
          } else {
            onChange(undefined);
          }
        }}
        options={[
          {
            value: 'text',
            label: t('phrase.文本'),
          },
          {
            value: 'material',
            label: t('素材'),
          },
        ]}
      />
    </SelectWrapper>
  );

  const refsCurrent = {
    type,
    value,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  return (
    <div>
      {select}
      {type === 'text' && value && value.type === 'text' && (
        <Input
          placeholder={t('department.请输入x', { x: t('phrase.文本') })}
          value={value.text}
          onChange={({ target: { value } }) =>
            onChange({
              type: 'text',
              text: value,
            })
          }
          onBlur={onBlur}
          showCount
          maxLength={60}
        />
      )}
      {type === 'material' && (
        <RadioContainer>
          <RadioWrapper
            onClick={() =>
              onChange({
                type: 'image',
                example: '',
                fileName: '',
                previewUrl: '',
              })
            }
          >
            <Radio checked={value?.type === 'image'}>{t('phrase.图片')}</Radio>
            <p>
              {t('支持x', { x: accepts.map((_) => `.${_}`).join(' ') })}
              <br />
              {t('最大x', { x: '5M' })}
            </p>
          </RadioWrapper>
          <RadioWrapper
            onClick={() =>
              onChange({
                type: 'video',
                example: '',
                fileName: '',
                previewUrl: '',
              })
            }
          >
            <Radio checked={value?.type === 'video'}>{t('phrase.视频')}</Radio>
            <p>
              {t('支持x', { x: '.mp4' })}
              <br />
              {t('最大x', { x: '20M' })}
            </p>
          </RadioWrapper>
          <RadioWrapper
            onClick={() =>
              onChange({
                type: 'document',
                example: '',
                fileName: '',
                previewUrl: '',
              })
            }
          >
            <Radio checked={value?.type === 'document'}>{t('文件')}</Radio>
            <p>
              {t('支持x', { x: '.pdf' })}
              <br />
              {t('最大x', { x: '20M' })}
            </p>
          </RadioWrapper>
        </RadioContainer>
      )}
      {(() => {
        if (!value || value.type === 'text') {
          return null;
        }

        const { type } = value;

        const Component = {
          image: ImageUploader,
          video: VideoUploader,
          document: PdfUploader,
        }[type];

        return (
          <UploadWrapper>
            <Component
              value={value}
              onChange={(next) => {
                onChange({
                  ...value,
                  ...next,
                });
              }}
            >
              <Button icon={<UploadOutlined />}>{t('上传示例')}</Button>
            </Component>
          </UploadWrapper>
        );
      })()}
    </div>
  );
}

export default HeaderEditor;
